import { Component, computed, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { mergePredefinedOnlineSelectionsWithCourses, OnlineCourseService } from '@lx/quote-tool/lite/online/services';
import { OnlineCourseSelectorComponent } from '@lx/quote-tool/lite/online/ui';
import { CopyQuoteSelectionsService, SidePanelService } from '@lx/quote-tool/lite/shared/services';
import { CourseDetails, SelectableField } from '@lx/quote-tool/lite/shared/types';
import { SidePanelComponent } from '@lx/quote-tool/lite/shared/ui/components';
import { TripDatesService } from '@lx/quote-tool/shared/services';
import { Course, PredefinedOnlineSelections } from '@lx/shared/data-access/types';
import { GoogleAnalyticsClickEventDirective } from '@lx/shared/google-analytics';
import { LoggerService } from '@lx/shared/logger';
import { ButtonComponent } from '@lx/shared/ui/button';
import { CalendarConfig } from '@lx/shared/ui/calendar-field';
import { DEFAULT_WEEKS_RANGE } from '@lx/shared/utils';
import { BreakpointObserverService } from '@lx/shared/utils-dom';
import { CardModule } from 'primeng/card';
import { combineLatest } from 'rxjs';
import { switchMap, tap } from 'rxjs/operators';
import { GoogleAnalyticsEventEnum } from '../../../google-analytics';
import { NavigationService, OnlineDeeplinkGeneratorService, OnlineSchoolResolverService } from '../../../services';
import { OnlineQuoteToolStore } from '../../../store';
import { CourseIntensitySelectorComponent } from '../../course-intensity-selector/course-intensity-selector.component';
import { CourseSummaryComponent } from '../../course-summary/course-summary.component';
import { DurationSelectorComponent } from '../../duration-selector/duration-selector.component';
import { LayoutComponent } from '../../layout/layout.component';
import { StartDateSelectorComponent } from '../../start-date-selector/start-date-selector.component';
import { StickyTotalPriceFooterComponent } from '../../sticky-total-price-footer/sticky-total-price-footer.component';

/**
 * Build Your Course Step Component
 *
 * Allows users to select and configure their online course with:
 * - Course selection (with comparison dialog)
 * - Duration selection (weeks counter)
 * - Start date selection (flexible calendar)
 *
 * ARCHITECTURAL PATTERN:
 * - Trip dates calculation is handled by OnlineQuoteToolStore.calculateTripDates$ effect
 * - Component only updates store state via updaters (updateSelectedDuration, updateSelectedStartDate)
 * - Store effect automatically responds to state changes and calculates end dates
 *
 * Benefits:
 * - Single source of truth for trip dates logic
 * - Automatic reactivity (no manual coordination needed)
 * - Prevents race conditions and duplicate renders
 */
@Component({
  standalone: true,
  selector: 'lx-build-your-course',
  templateUrl: './build-your-course.component.html',
  styleUrls: ['./build-your-course.component.scss'],
  imports: [
    LayoutComponent,
    CourseSummaryComponent,
    ButtonComponent,
    CardModule,
    SidePanelComponent,
    OnlineCourseSelectorComponent,
    CourseIntensitySelectorComponent,
    DurationSelectorComponent,
    StartDateSelectorComponent,
    GoogleAnalyticsClickEventDirective,
    StickyTotalPriceFooterComponent,
  ],
})
export class BuildYourCourseComponent implements OnInit {
  // Inject services first (before using them)
  private readonly navigationService = inject(NavigationService);
  private readonly store = inject(OnlineQuoteToolStore);
  private readonly breakpointObserverService = inject(BreakpointObserverService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly onlineSchoolResolverService = inject(OnlineSchoolResolverService);
  private readonly onlineCourseService = inject(OnlineCourseService);
  private readonly sidePanelService = inject(SidePanelService);
  private readonly tripDatesService = inject(TripDatesService); // Only for calendar config
  private readonly loggerService = inject(LoggerService);
  private readonly deeplinkService = inject(OnlineDeeplinkGeneratorService);
  private readonly copyService = inject(CopyQuoteSelectionsService);

  // Signals for reactive state management
  protected readonly selectedCourse = toSignal(this.store.selectedCourse$);
  protected readonly selectedDuration = toSignal(this.store.selectedDuration$);
  protected readonly selectedStartDate = toSignal(this.store.selectedStartDate$);
  protected readonly selectedEndDate = toSignal(this.store.selectedEndDate$);
  protected readonly totalPrice = toSignal(this.store.totalPrice$);
  protected readonly currency = toSignal(this.store.currency$);
  protected readonly courseSummary = toSignal(this.store.courseSummary$);

  protected courseSelectorDialogVisible = signal(false);
  protected loading = signal<boolean>(true);

  protected onlineSchoolCode = signal<string | undefined>(undefined);
  protected calendarConfig = signal<CalendarConfig | undefined>(undefined);

  // Available courses loaded from API
  protected availableCourses = signal<Course[]>([]);
  protected predefinedSelections = signal<PredefinedOnlineSelections[]>([]);

  /**
   * Reactive computation of min weeks based on selected course
   * Automatically updates when course changes
   * Falls back to DEFAULT_WEEKS_RANGE if no course selected or no durationMin specified
   */
  protected readonly minWeeks = computed(() => {
    const course = this.selectedCourse();
    return course?.durationMin ?? DEFAULT_WEEKS_RANGE.MIN_WEEKS;
  });

  /**
   * Reactive computation of max weeks based on selected course
   * Automatically updates when course changes
   * Falls back to DEFAULT_WEEKS_RANGE if no course selected or no durationMax specified
   */
  protected readonly maxWeeks = computed(() => {
    const course = this.selectedCourse();
    return course?.durationMax ?? DEFAULT_WEEKS_RANGE.MAX_WEEKS;
  });

  // Merged course options for comparison dialog
  protected readonly courseOptions = computed(() => mergePredefinedOnlineSelectionsWithCourses(this.predefinedSelections(), this.availableCourses()));
  protected readonly title = computed(() => this.currentStep()?.title);
  protected readonly subtitle = computed(() => this.currentStep()?.subtitle);
  protected readonly shareableDeeplink = computed(() => this.currentStep()?.shareableDeeplink ?? false);
  protected readonly shareableLinkAvailability = toSignal(this.deeplinkService.valid$, { initialValue: false });
  protected readonly shareableLinkCopyStatus = this.copyService.copied;

  protected readonly courseName = computed(() => this.selectedCourse()?.name ?? '');

  protected readonly courseDetails = computed(() => {
    const course = this.selectedCourse();
    const duration = this.selectedDuration();
    const startDate = this.selectedStartDate();
    const endDate = this.selectedEndDate();
    const price = this.totalPrice();

    return {
      courseName: course?.name,
      duration,
      startDate,
      endDate,
      price: price?.value,
      currency: this.currency() || price?.currency,
    };
  });

  protected readonly formValid = computed(() => {
    return !!this.selectedCourse() && !!this.selectedDuration() && !!this.selectedStartDate();
  });

  protected readonly mobile = toSignal(this.breakpointObserverService.isMobile$);

  // Google Analytics event tracking
  protected readonly bookNowEventName = GoogleAnalyticsEventEnum.QuoteBookNow;

  private readonly currentStep = toSignal(this.store.currentStep$);

  ngOnInit(): void {
    this.loading.set(true);

    // Load school code, then load all related data in parallel
    this.onlineSchoolResolverService
      .getOnlineSchoolCode()
      .pipe(
        tap((schoolCode) => this.onlineSchoolCode.set(schoolCode)),
        switchMap((schoolCode) =>
          combineLatest([
            this.onlineCourseService.getOnlineCourses(schoolCode),
            this.onlineCourseService.getPredefinedOnlineSelections(schoolCode),
            this.tripDatesService.getCalendarConfig(schoolCode),
          ]),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: ([courses, selections, calendarConfig]) => {
          // Update all signals with loaded data
          this.availableCourses.set(courses);
          this.predefinedSelections.set(selections);
          this.calendarConfig.set(calendarConfig);

          // Auto-select a predefined course if none selected
          if (!this.selectedCourse()) {
            this.autoSelectPredefinedCourse();
          }

          this.loading.set(false);
        },
        error: (error) => {
          this.loading.set(false);
          this.loggerService.error('Failed to fetch data for loading online courses', error);
        },
      });
  }

  // Handle copy requested from child layout component
  protected handleCopyRequested(): void {
    this.copyService.handleCopyRequested(this.deeplinkService.generateDeeplink());
  }

  /**
   * Handles course selection from comparison dialog.
   * Iit will close the dialog and update the store with the selected course.
   */
  protected selectCourseFromComparedCourses(event: SelectableField<CourseDetails>): void {
    this.sidePanelService.hidePanel(() => {
      this.courseSelectorDialogVisible.set(false);

      // Find the full course object from available courses
      const fullCourse = this.availableCourses().find((c) => c.code === event.value?.code);
      if (!fullCourse) {
        return;
      }

      this.selectCourse(fullCourse);
    });
  }

  /**
   * Handles direct course selection from option cards
   * Duration constraints (minWeeks, maxWeeks) update automatically via computed signals
   */
  protected selectCourse(course: Course): void {
    this.store.updateSelectedCourse(course);
  }

  /**
   * Handles duration changes from weeks counter.
   * Updates the store with the selected duration.
   */
  protected selectDuration(weeks: number): void {
    this.store.updateSelectedDuration(weeks);
  }

  /**
   * Handles start date changes from date selector.
   * Updates the store with the selected start date.
   */
  protected selectStartDate(selectedStartDate: string): void {
    this.store.updateSelectedStartDate(selectedStartDate);
  }

  /**
   * Opens the course comparison dialog
   */
  protected openCourseSelector(): void {
    this.courseSelectorDialogVisible.set(true);
  }

  /**
   * Closes the course comparison dialog
   */
  protected closeCourseSelector(): void {
    this.courseSelectorDialogVisible.set(false);
  }

  /**
   * Handles Book Now button click with validation
   */
  protected onBookNow(): void {
    // Validate all selections are present
    if (!this.formValid()) {
      return;
    }

    if (this.currentStep()) {
      this.navigationService.navigateToNextStep(this.currentStep());
    }
  }

  /**
   * Auto-select a predefined course.
   *
   * Selection priority:
   * 1. lowestPriced
   * 2. mostPopular
   * 3. first predefined selection
   */
  private autoSelectPredefinedCourse(): void {
    const selections = this.predefinedSelections();
    const preferredSelection = this.findPreferredSelection(selections);

    if (!preferredSelection?.courseId) {
      return;
    }

    const { courseId, duration, startDate } = preferredSelection;

    // Find the full course object from available courses
    const course = this.availableCourses().find((c) => c.code === courseId);

    if (!course) {
      return;
    }

    this.selectCourse(course);
    this.selectDuration(duration);
    this.selectStartDate(startDate);
  }

  /**
   * Find the preferred selection.
   * Selection priority:
   * 1. lowestPriced
   * 2. mostPopular
   * 3. first predefined selection
   * @param selections the list of available selections
   */
  private findPreferredSelection(selections: PredefinedOnlineSelections[]): PredefinedOnlineSelections | undefined {
    if (selections?.length === 0) {
      return undefined;
    }

    return selections.find(({ lowestPriced }) => lowestPriced) ?? selections.find(({ mostPopular }) => mostPopular) ?? selections[0];
  }
}
